// DiaryLikeButton — inline heart button bound to ``useDiaryLikes``.
//
// Render inside diary cards, detail header, or anywhere a per-diary like
// toggle is needed.  Optimistic toggle per W3 §8.3.

import { Heart } from "lucide-react";

import { cn } from "@/lib/utils/cn";
import { useDiaryLikes } from "@/hooks/useDiaryLikes";

interface DiaryLikeButtonProps {
  diaryId: string;
  className?: string;
  /** Show the count beside the heart. Defaults to true. */
  showCount?: boolean;
}

export function DiaryLikeButton({ diaryId, className, showCount = true }: DiaryLikeButtonProps) {
  const { liked, count, busy, toggle } = useDiaryLikes(diaryId);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle();
      }}
      disabled={busy}
      aria-pressed={liked}
      aria-label={liked ? "取消讚" : "按讚"}
      data-testid="diary-like-button"
      data-liked={liked ? "true" : "false"}
      className={cn(
        // R0 (W39.0): measured 36×24 — the heart and its count were a 24px
        // strip. The button sits inline inside a diary card's meta row, so
        // growing the box with `min-h-11` would reflow the row's rhythm on
        // every diary. Instead the target is extended with the same `::after`
        // overlay the calendar chips use (MonthGrid.tsx): the pill keeps its
        // size and only the tappable area reaches 44pt. Width is 36px already,
        // and the heart is the leftmost control in its row, so there is no
        // neighbour to mis-tap into.
        "press relative inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-body-sm transition-colors",
        "after:absolute after:left-1/2 after:top-1/2 after:h-11 after:w-full after:min-w-11",
        "after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
        liked ? "bg-you/10 text-you-strong" : "text-ink-3 hover:bg-black/5 hover:text-ink-1",
        className,
      )}
    >
      <Heart
        size={16}
        aria-hidden
        className={cn("transition-colors", liked && "fill-you-strong text-you-strong")}
      />
      {showCount && count > 0 ? (
        <span data-testid="diary-like-count" className="text-body-sm">
          {count}
        </span>
      ) : null}
    </button>
  );
}
